import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { performanceReviewService, userService } from '../../services/api';
import { FiPlus, FiEye } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import './ReviewList.css';

const STATUS_OPTIONS = ['draft', 'submitted', 'acknowledged'];

export const ReviewList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(user?.id || '');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'manager') {
      fetchEmployees();
    }
  }, [user?.role]);

  useEffect(() => {
    fetchReviews();
  }, [selectedEmployee, filterStatus]);

  const fetchEmployees = async () => {
    try {
      const response = await userService.getAll({ limit: 1000 });
      setEmployees(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchReviews = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await performanceReviewService.getAll({
        userId: selectedEmployee,
        status: filterStatus === 'all' ? undefined : filterStatus,
      });
      setReviews(response.data.reviews || []);
    } catch (err) {
      setError('Failed to load reviews');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewReview = (reviewId) => {
    navigate(`/performance-review/${reviewId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'status-draft';
      case 'submitted':
        return 'status-submitted';
      case 'acknowledged':
        return 'status-acknowledged';
      default:
        return 'status-draft';
    }
  };

  const getStarRating = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= rating ? 'star filled' : 'star'}>
          ★
        </span>
      );
    }
    return stars;
  };

  const canCreateReview = user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="review-list-container">
      <div className="review-header">
        <h1>Performance Reviews</h1>
        {canCreateReview && (
          <button
            className="btn btn-primary"
            onClick={() => navigate('/performance-review/new')}
          >
            <FiPlus /> New Review
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {(user?.role === 'admin' || user?.role === 'manager') && (
        <div className="filters-section">
          <div className="filter-group">
            <label>Employee:</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value={user?.id}>My Reviews</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Status:</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading reviews...</div>
      ) : reviews.length > 0 ? (
        <div className="reviews-table-wrapper">
          <table className="reviews-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Reviewer</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((review) => (
                <tr key={review.id}>
                  <td className="period-cell">
                    {format(parseISO(review.reviewPeriodStart), 'MMM yyyy')} -{' '}
                    {format(parseISO(review.reviewPeriodEnd), 'MMM yyyy')}
                  </td>
                  <td>{review.reviewer ? `${review.reviewer.firstName} ${review.reviewer.lastName}` : 'Manager'}</td>
                  <td>
                    <div className="rating-stars">
                      {getStarRating(review.rating || 0)}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusColor(review.status)}`}>
                      {review.status}
                    </span>
                  </td>
                  <td>{format(parseISO(review.createdAt), 'MMM d, yyyy')}</td>
                  <td>
                    <button
                      className="btn-view"
                      onClick={() => handleViewReview(review.id)}
                      title="View review"
                    >
                      <FiEye />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-reviews">
          <p>No performance reviews yet</p>
          {canCreateReview && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/performance-review/new')}
            >
              <FiPlus /> Create First Review
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewList;
